/**
 * RankShareModal — opens from the /leaderboard "Share my rank" CTA.
 * Mounts a RankShareCardRender off-screen, snapshots it via html-to-image,
 * then offers WhatsApp / Telegram / native share / download options.
 *
 * Tap-driven (per the no-typing design memory): the share text is
 * pre-composed; students just pick a target.
 */

import React, { useEffect, useRef, useState } from 'react';
import RankShareCardRender, { RankShareCardProps } from './RankShareCardRender';
import {
  captureShareCardNode,
  downloadBundle,
  nativeShareBundle,
  ShareCardBundle,
} from './ShareCard';
import { authFetch } from '../services/authFetch';
import './RankShareModal.css';

/**
 * Fire-and-forget telemetry — best effort, never blocks the share. Lands
 * in engagement_events via /telemetry/event. The /ops dashboards filter on
 * event_name='rank_share.*' to read share counts.
 */
const trackShare = (name: string, props: Record<string, unknown>): void => {
  try {
    authFetch('/api/telemetry/event', {
      method: 'POST',
      body: JSON.stringify({ name, properties: props }),
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
};

/**
 * Pre-fetch an external avatar URL and convert to a data: URL so the
 * canvas-based capture in html-to-image doesn't get CORS-tainted.
 * Returns null if the fetch or read fails — the share card falls back to
 * its initials gradient in that case.
 *
 * Same-origin URLs are returned as-is (no fetch needed).
 */
async function fetchAvatarAsDataUrl(url: string): Promise<string | null> {
  try {
    // Same-origin paths embed fine through html-to-image — return verbatim
    if (url.startsWith('/')) return url;
    const resp = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

interface Props {
  open: boolean;
  onClose: () => void;
  card: RankShareCardProps;
}

const RankShareModal: React.FC<Props> = ({ open, onClose, card }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [bundle, setBundle] = useState<ShareCardBundle | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Avatar inlined as a data: URL — html-to-image's canvas pipeline gets
  // CORS-tainted by external URLs (Google's lh3 CDN works on mobile but
  // not desktop). Pre-fetching to a data URL sidesteps that.
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);

  // Pre-compose the share text once. Students never type.
  const shareText =
    `I'm #${card.rank} on Lomi 🍋 ESSLCE prep. ` +
    `${card.bestCombo > 0 ? `⚡ ${card.bestCombo} in a row · ` : ''}` +
    `${card.streakDays > 0 ? `🔥 ${card.streakDays}-day streak · ` : ''}` +
    `${card.xp.toLocaleString()} points. Try it: ${card.url}`;

  // Capture once on open. The card mounts off-screen first frame so KaTeX /
  // QR have time to settle before toBlob runs.
  useEffect(() => {
    if (!open) return;
    // Telemetry: opened. Fires once per modal open. Variant + subject
    // tell us which share button (overall vs subject-specific) drove it.
    trackShare('rank_share.opened', {
      variant: card.variant || 'overall',
      subject: card.subjectName,
      rank: card.variant === 'subject' ? card.subjectRank : card.rank,
    });
    let cancelled = false;
    setBundle(null);
    setErr(null);
    setAvatarDataUrl(null);
    setBusy(true);
    const captureWhenReady = async () => {
      try {
        // Pre-fetch the avatar as a data URL when the source is external.
        // Same-origin paths (/api/student/profile/avatar/...) embed fine
        // via html-to-image; cross-origin (Google, Telegram) need CORS-
        // mode fetch + base64 conversion or the canvas gets tainted.
        if (card.avatarUrl) {
          const dataUrl = await fetchAvatarAsDataUrl(card.avatarUrl);
          if (!cancelled) setAvatarDataUrl(dataUrl);
        }
        // Two RAF ticks gives the off-screen card time to lay out (now
        // including the inlined avatar) before we ask html-to-image to
        // serialize it.
        await new Promise(r => requestAnimationFrame(() => r(null)));
        await new Promise(r => requestAnimationFrame(() => r(null)));
        if (!cardRef.current) return;
        const b = await captureShareCardNode(cardRef.current, `lomi-rank-${Date.now()}.png`);
        if (!cancelled) setBundle(b);
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || 'Could not render share card.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    };
    captureWhenReady();
    return () => {
      cancelled = true;
    };
  }, [open, card.rank, card.xp, card.streakDays, card.bestCombo, card.totalRanked, card.avatarUrl]);

  // Cleanup the object URL when the modal closes.
  useEffect(() => {
    return () => {
      if (bundle?.objectUrl) URL.revokeObjectURL(bundle.objectUrl);
    };
  }, [bundle]);

  // Esc to close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const enc = (s: string) => encodeURIComponent(s);
  const whatsappUrl = `https://wa.me/?text=${enc(shareText)}`;
  const telegramUrl = `https://t.me/share/url?url=${enc(card.url)}&text=${enc(shareText)}`;

  const onNativeShare = async () => {
    if (!bundle) return;
    trackShare('rank_share.channel_clicked', {
      channel: 'native',
      variant: card.variant || 'overall',
      subject: card.subjectName,
    });
    const result = await nativeShareBundle(bundle, {
      questionText: shareText,
      url: card.url,
    });
    if (result === 'shared') {
      trackShare('rank_share.completed', {
        channel: 'native',
        variant: card.variant || 'overall',
        subject: card.subjectName,
      });
      onClose();
    }
  };

  return (
    <div className="rsm-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="rsm-card" onClick={(e) => e.stopPropagation()}>
        <button className="rsm-close" onClick={onClose} aria-label="Close">×</button>
        <h3 className="rsm-title">Share your rank</h3>

        {/* Preview — scaled down to fit the modal */}
        <div className="rsm-preview-wrap">
          {bundle ? (
            <img className="rsm-preview-img" src={bundle.objectUrl} alt="Share card preview" />
          ) : err ? (
            <div className="rsm-preview-err">{err}</div>
          ) : (
            <div className="rsm-preview-skeleton" aria-busy="true">
              {busy ? 'Rendering your card…' : ''}
            </div>
          )}
        </div>

        {/* Tap targets — no typing */}
        <div className="rsm-actions">
          <a
            className="rsm-btn rsm-btn-whatsapp"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackShare('rank_share.channel_clicked', {
                channel: 'whatsapp',
                variant: card.variant || 'overall',
                subject: card.subjectName,
              });
              onClose();
            }}
          >
            <span className="rsm-btn-icon">💬</span>
            <span>WhatsApp</span>
          </a>
          <a
            className="rsm-btn rsm-btn-telegram"
            href={telegramUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackShare('rank_share.channel_clicked', {
                channel: 'telegram',
                variant: card.variant || 'overall',
                subject: card.subjectName,
              });
              onClose();
            }}
          >
            {/* Telegram brand paper-plane (inline SVG) — emoji ✈️ rendered
                inconsistently across platforms and read as a generic plane
                rather than the brand mark. */}
            <svg
              className="rsm-btn-icon-svg"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.247-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
            <span>Telegram</span>
          </a>
          {bundle?.canNativeShare && (
            <button
              className="rsm-btn rsm-btn-share"
              onClick={onNativeShare}
              disabled={!bundle}
            >
              <span className="rsm-btn-icon">↗</span>
              <span>More…</span>
            </button>
          )}
          <button
            className="rsm-btn rsm-btn-download"
            onClick={() => {
              if (!bundle) return;
              trackShare('rank_share.channel_clicked', {
                channel: 'download',
                variant: card.variant || 'overall',
                subject: card.subjectName,
              });
              downloadBundle(bundle);
            }}
            disabled={!bundle}
          >
            <span className="rsm-btn-icon">⬇</span>
            <span>Download</span>
          </button>
        </div>

        <p className="rsm-tip">
          The image works for IG / TikTok stories. Text-only links go to WhatsApp & Telegram.
        </p>

        {/* Off-screen DOM for capture. left: -99999px keeps fonts loaded but
            invisible to the user. The avatar URL is replaced with the
            CORS-safe data URL when one was successfully pre-fetched. */}
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: '-99999px',
            top: 0,
            pointerEvents: 'none',
          }}
        >
          <RankShareCardRender
            ref={cardRef}
            {...card}
            avatarUrl={avatarDataUrl ?? (card.avatarUrl?.startsWith('/') ? card.avatarUrl : null)}
          />
        </div>
      </div>
    </div>
  );
};

export default RankShareModal;
